import React, { useState } from "react";
import { Button, Empty, Typography, Modal, Tag, Divider } from "antd";
import {
  FileSearchOutlined,
  EyeOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { JobApplication } from "../../../../Entities/JobApplication";
import { MatchResult } from "../../../../Entities/MatchResult";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import { UserProfile } from "../../../../Entities/UserProfile";
import useCountUp from "../../../../Utils/countUpAnimation";
import { isProfileMatchCompleted } from "../../utils/job.application.helper.functions";
import ResumeUploadModal from "./ResumeUploadModal";
import { openNotification } from "../../../../Components/Notification";
import { useLoader } from "../../../../Context/LoaderContext";
import profileService from "../../../../Service/profile.service";
import { classifyError } from "../../../../Utils/errorClassifier";
import { ERROR_MESSAGES } from "../../../../Utils/errorMessages";

interface ProfileMatchProps {
  jobApplication: JobApplication;
  profileMatchResult: MatchResult | null;
  jobPost: JobDescriptionEntity;
  profile: UserProfile | null;
  setActiveTabKey: React.Dispatch<React.SetStateAction<string>>;
  setIsResumeViewing: React.Dispatch<
    React.SetStateAction<{
      isResumeViewing: boolean;
      resumeUrl: string | null;
    }>
  >;
  handleSendReminderNotification: (type: "profile.match" | "round") => Promise<void>;
  setProfileMatchResult: React.Dispatch<
    React.SetStateAction<MatchResult | null>
  >;
  setSelectedApplication: React.Dispatch<
    React.SetStateAction<JobApplication | null>
  >;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
}

const ProfileMatch: React.FC<ProfileMatchProps> = ({
  jobApplication,
  profileMatchResult,
  jobPost,
  profile,
  setActiveTabKey,
  setIsResumeViewing,
  handleSendReminderNotification,
  setProfileMatchResult,
  setSelectedApplication,
  setUserProfile,
}) => {
  const [isResumeUploadModalOpen, setIsResumeUploadModalOpen] = useState(false);
  const [uploadError, setUploadError] = useState<any>(null);
  const { setLoading, setLoadingMessage } = useLoader();

  const totalRequirements = jobPost?.requirements?.length || 0;
  const profileMatchResults = profileMatchResult?.results || [];

  const fullyMatched = profileMatchResults.filter(
    (result) => result?.match_status === 1
  ).length;

  const partiallyMatched = profileMatchResults.filter(
    (result) => result?.match_status === 0
  ).length;

  const unqualified = profileMatchResults.filter(
    (result) => result?.match_status === -1
  ).length;

  const totalRequirementsCount = useCountUp(
    totalRequirements,
    1200,
    0,
    "easeOut"
  );

  const fullyMatchedCount = useCountUp(fullyMatched, 1200, 0, "easeOut");
  const partiallyMatchedCount = useCountUp(
    partiallyMatched,
    1200,
    0,
    "easeOut"
  );
  const unqualifiedCount = useCountUp(unqualified, 1200, 0, "easeOut");

  const handleViewAIAnalysis = () => {
    setActiveTabKey("profile-match");
  };

  const handleViewResume = () => {
    if (!profile) {
      console.error(
        "Job Application Summary -> ProfileMatch -> handleViewResume: Resume not found"
      );
      return;
    }
    const resumeUrl = `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/profile/${profile.userId}/${profile.filename}`;
    setIsResumeViewing({
      isResumeViewing: true,
      resumeUrl: resumeUrl,
    });
  };

  const handleCloseResumeUploadModal = () => {
    setIsResumeUploadModalOpen(false);
    setUploadError(null);
  };

  // Use single source of truth helper function
  const isProfileMatchDone = isProfileMatchCompleted(jobApplication);

  // Only show resume upload CTAs if:
  // 1. Profile match is not done AND
  // 2. No profile/resume exists AND
  // 3. Application status is "applied"
  const shouldShowResumeUploadCTAs =
    !isProfileMatchDone && 
    !profile && 
    !profileMatchResult &&
    jobApplication?.status === "applied";

  const handleSendEmailClick = () => {
    Modal.confirm({
      centered: true,
      okText: "Send Email",
      cancelText: "Cancel",
      icon: null,
      title: "Send Email ?",
      content:
        "Are you sure you want to send an email to the candidate reminding them to upload their resume?",
      onOk: async () => {
        await handleSendReminderNotification("profile.match");
      },
      onCancel: () => {
        return;
      },
    });
  };

  const handleUploadResumeAndMatch = async (file: any) => {
    if (!jobApplication?._id) {
      console.error(
        "Job Application Summary -> ProfileMatch -> handleUploadResume: Job application ID not found"
      );
      openNotification("error", "Error", "Job application ID not found");
      return;
    }
    try {
      setUploadError(null);
      setLoading(true);
      setLoadingMessage("Uploading resume and matching profile...");

      const response = await profileService.matchResume(
        jobApplication._id,
        file
      );

      if (response?.status === false) {
        // Soft-error pattern: classify and display
        const classified = classifyError(response);
        const msgs = ERROR_MESSAGES[classified.category];
        setUploadError({
          classified,
          headline: msgs.recruiterHeadline,
          body: msgs.recruiterBody,
          guidance: msgs.recruiterGuidance,
          isRetryable: classified.isRetryable,
        });
        return;
      }

      setProfileMatchResult(response?.data?.matchResults);
      setSelectedApplication(response?.data?.jobApplication);
      setActiveTabKey("profile-match");
      setUserProfile(response?.data?.userProfile);
      openNotification(
        "success",
        "Resume uploaded and profile matched successfully",
        ""
      );
      handleCloseResumeUploadModal();
    } catch (error: any) {
      console.error(
        "Job Application Summary -> ProfileMatch -> handleUploadResumeAndMatch ~ error:",
        error
      );
      // Classify the error and display structured message
      const classified = classifyError(error);
      const msgs = ERROR_MESSAGES[classified.category];
      setUploadError({
        classified,
        headline: msgs.recruiterHeadline,
        body: msgs.recruiterBody,
        guidance: msgs.recruiterGuidance,
        isRetryable: classified.isRetryable,
      });
      return;
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  return (
    <section>
      <div className="job-application-summary-content-card">
        <div className="job-application-summary-content-card-header">
          <Typography.Title level={5} style={{ margin: 0 }}>
            <FileSearchOutlined style={{ color: "var(--ant-color-primary)" }} />
            &nbsp; Profile Match
          </Typography.Title>
        </div>

        <div className="profile-match-content-container">
          {profileMatchResult && (
            <div className="profile-match-total-requirements">
              <Typography.Title
                level={4}
                style={{
                  margin: 0,
                  fontWeight: 500,
                }}
              >
                {totalRequirementsCount.count}
              </Typography.Title>
              <Typography.Text strong>Total Requirements</Typography.Text>
            </div>
          )}

          {profileMatchResult && (
            <div className="profile-match-result-statistics-wrapper">
              <div className="profile-match-result-statistics-item statistics-item-success">
                <Typography.Title
                  level={4}
                  style={{
                    margin: 0,
                    fontWeight: 500,
                    color: "var(--ant-color-success)",
                  }}
                >
                  {fullyMatchedCount.count}
                </Typography.Title>
                <Typography.Text strong>Match</Typography.Text>
              </div>
              <div className="profile-match-result-statistics-item statistics-item-warning">
                <Typography.Title
                  level={4}
                  style={{
                    margin: 0,
                    fontWeight: 500,
                    color: "var(--ant-color-warning)",
                  }}
                >
                  {partiallyMatchedCount.count}
                </Typography.Title>
                <Typography.Text strong>Partial</Typography.Text>
              </div>
              <div className="profile-match-result-statistics-item statistics-item-error">
                <Typography.Title
                  level={4}
                  style={{
                    margin: 0,
                    fontWeight: 500,
                    color: "var(--ant-color-error)",
                  }}
                >
                  {unqualifiedCount.count}
                </Typography.Title>
                <Typography.Text strong>Unmatch</Typography.Text>
              </div>
            </div>
          )}

          {shouldShowResumeUploadCTAs && (
            <div className="summary-card-placeholder ">
              <Empty
                description="The candidate has not uploaded a resume yet."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
              <div className="profile-match-upload-button-container">
                {/* <Button
                  type="default"
                  icon={<UploadOutlined />}
                  onClick={handleOpenResumeUploadModal}
                >
                  Upload Resume
                </Button> */}
                <Button
                  type="primary"
                  icon={<MailOutlined />}
                  onClick={handleSendEmailClick}
                  style={{ marginLeft: "8px" }}
                >
                  Send Reminder Email
                </Button>
              </div>
            </div>
          )}

          {!isProfileMatchDone && !profileMatchResult && !shouldShowResumeUploadCTAs && (
            <div className="summary-card-placeholder">
              <Empty
                description="The candidate has not uploaded a resume yet."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}

          {profile && (profile as any).skills && Array.isArray((profile as any).skills) && (profile as any).skills.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Divider orientation="left" style={{ margin: "12px 0" }}>
                <Typography.Text strong>Skills</Typography.Text>
              </Divider>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {((profile as any).skills || []).map((skill: string, index: number) => (
                  <Tag key={index} color="blue">
                    {skill}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {/* {profile && (profile as any).experience && Array.isArray((profile as any).experience) && (profile as any).experience.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Divider orientation="left" style={{ margin: "12px 0" }}>
                <Typography.Text strong>Experience</Typography.Text>
              </Divider>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {((profile as any).experience || []).map((exp: any, index: number) => (
                  <div key={index} style={{ padding: "8px", background: "#f0f2f5", borderRadius: "4px" }}>
                    <Typography.Text strong>{exp.title || exp.position || "Position"}</Typography.Text>
                    {exp.company && (
                      <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                        at {exp.company}
                      </Typography.Text>
                    )}
                    {exp.duration && (
                      <Typography.Text type="secondary" style={{ display: "block", marginTop: 4 }}>
                        {exp.duration}
                      </Typography.Text>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )} */}

          {(profile || profileMatchResult) && (
            <div className="profile-match-result-actions">
              {profile ? (
                <Button
                  type="default"
                  icon={<EyeOutlined />}
                  onClick={handleViewResume}
                >
                  View Resume
                </Button>
              ) : (
                <div></div>
              )}

              {profileMatchResult ? (
                <Button
                  type="primary"
                  icon={
                    <img
                      src="/icons/ai-stars.svg"
                      alt="AI"
                      style={{ width: "14px", height: "14px" }}
                      className="ai-icon-shine"
                    />
                  }
                  onClick={handleViewAIAnalysis}
                >
                  AI Analysis
                </Button>
              ) : (
                <div></div>
              )}
            </div>
          )}
        </div>
      </div>
      <ResumeUploadModal
        isOpen={isResumeUploadModalOpen}
        onClose={handleCloseResumeUploadModal}
        handleUpload={handleUploadResumeAndMatch}
        uploadError={uploadError}
      />
    </section>
  );
};

export default ProfileMatch;
